// reportData.js — shared engine for Owner + Management reports.
// Gathers a month's figures from every module. ALL numbers computed here in-app
// so the AI summary can only narrate them, never invent them.
import { supabase } from './supabase'
import { fetchRows, fetchCrewDb } from './api'
import { SECTIONS, ALL_ROWS } from './monthlyReview'
import { crewCounts, isOnBoard, crewFact } from './crewCount'
import { budgetFigures } from './fleetReport'

const monthOf = (iso) => String(iso || '').slice(0, 7)
const gv = (v) => {
  if (v == null) return ''
  if (typeof v === 'object') return v.value || v.name || v.label || (Array.isArray(v) && v[0] ? (v[0].value || v[0].name || '') : '')
  return String(v)
}
const inMonth = (iso, month) => monthOf(iso) === month

// Pull a monetary value from a row, trying the field names charters commonly use
const MONEY_KEYS = ['Revenue', 'Charter Fee', 'Fee', 'Rate', 'Gross', 'Total', 'Amount', 'Price', 'Charter Rate', 'Value']
function moneyOf(row) {
  for (const k of MONEY_KEYS) {
    const raw = gv(row[k])
    if (raw === '' || raw == null) continue
    const n = Number(String(raw).replace(/[^0-9.-]/g, ''))
    if (!isNaN(n) && n !== 0) return n
  }
  return 0
}

// Parse a date-only string at local noon (avoids UTC off-by-one)
function parseLocal(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''))
  if (!m) { const t = Date.parse(s); return isNaN(t) ? null : new Date(t) }
  return new Date(+m[1], +m[2] - 1, +m[3], 12)
}

// ── Monthly-review compliance detection (reuses the review's SECTIONS engine) ──
async function gatherCompliance(clientId, month) {
  const detected = {}
  const [{ data: lsaItems }, { data: chkDefs }] = await Promise.all([
    supabase.from('lsa_items').select('id,name,type,location').eq('client_id', clientId).limit(1000),
    supabase.from('checklists').select('id,title').limit(2000),
  ])
  const lsaName = {}; for (const it of (lsaItems || [])) lsaName[it.id] = it.name || it.type || it.location || it.id
  const chkTitle = {}; for (const c of (chkDefs || [])) chkTitle[c.id] = c.title || 'Checklist'

  const NO_CLIENT_ID = new Set(['lsa_checks'])
  const sb = ALL_ROWS.filter(r => r.src === 'supabase')
  const sbTables = [...new Set(sb.map(r => r.table))]
  const sbData = {}
  await Promise.all(sbTables.map(async (t) => {
    let q = supabase.from(t).select('*').limit(1000)
    if (!NO_CLIENT_ID.has(t)) q = q.eq('client_id', clientId)
    const { data } = await q
    sbData[t] = data || []
  }))

  for (const r of sb) {
    const rows = sbData[r.table] || []
    const matches = rows.filter(row => {
      if (r.drillMatch && !new RegExp(r.drillMatch, 'i').test(String(row.drill_type || ''))) return false
      if (r.monthField) return String(row[r.monthField] || '') === month
      if (r.dateField) return inMonth(row[r.dateField], month)
      return false
    })
    detected[r.key] = { done: matches.length > 0, count: matches.length }
  }
  return { detected, sections: SECTIONS }
}

// ── Main entry: build the full report data object for a month ──
// config: the vessel (its id, vessel_name, flag, imo). The Fleet page passes each fleet vessel in turn (fleet-2), so every read
// names that vessel's id - inside a vessel config.id is the open vessel, the same rows as before.
export async function buildReportData({ config, month }) {
  const clientId = config?.id
  const [year] = month.split('-')

  // Parallel fetch across modules
  const [
    equipment, tasks, inventory, docs,
    charters, portCalls, engineLog,
    complianceRes,
    budgetLinesRes,
    crewRes, lsaRes, drillsRes, riskRes, refitRes,
  ] = await Promise.all([
    fetchRows('equipment', clientId).catch(() => []),
    fetchRows('mtask', clientId).catch(() => []),
    fetchRows('inventory', clientId).catch(() => []),
    fetchRows('docs', clientId).catch(() => []),
    fetchRows('charters', clientId).catch(() => []),
    fetchRows('port_calls', clientId).catch(() => []),
    fetchRows('engine_log', clientId).catch(() => []),
    gatherCompliance(clientId, month).catch(() => ({ detected: {}, sections: SECTIONS })),
    supabase.from('budget_lines').select('*').eq('client_id', clientId).then(r => r.data || []).catch(() => []),
    fetchCrewDb(clientId).catch(() => []),
    supabase.from('lsa_items').select('*').eq('client_id', clientId).then(r => r.data || []).catch(() => []),
    supabase.from('drill_records').select('*').eq('client_id', clientId).then(r => r.data || []).catch(() => []),
    supabase.from('risk_assessments').select('*').eq('client_id', clientId).then(r => r.data || []).catch(() => []),
    supabase.from('refit_items').select('*').eq('client_id', clientId).then(r => r.data || []).catch(() => []),
  ])

  // ── EQUIPMENT ──
  const eqTotal = equipment.length
  const eqOos = equipment.filter(e => (gv(e['Status']) || '').toLowerCase().includes('out of service'))
  const equipmentSummary = { total: eqTotal, operational: eqTotal - eqOos.length, outOfService: eqOos.length, oosList: eqOos.map(e => gv(e['Name'])).filter(Boolean) }

  // ── MAINTENANCE (this month) ──
  const overdue = tasks.filter(t => (gv(t['Status']) || '').toLowerCase() === 'overdue')
  const dueThisMonth = tasks.filter(t => inMonth(gv(t['Next Service Date']), month))
  const maintenance = { totalTasks: tasks.length, overdue: overdue.length, dueThisMonth: dueThisMonth.length, upcomingList: dueThisMonth.map(t => gv(t['Task Name']) || gv(t['Name'])).filter(Boolean).slice(0, 20) }

  // ── INVENTORY ──
  const invStatus = (i) => (gv(i['Status']) || '').toLowerCase()
  const inventorySummary = {
    total: inventory.length,
    outOfStock: inventory.filter(i => invStatus(i).includes('out of stock')).length,
    lowStock: inventory.filter(i => invStatus(i).includes('low')).length,
  }

  // ── DOCUMENTS / CERTS ──
  const docStatus = (d) => (gv(d['Status']) || '').toLowerCase()
  const documentsSummary = {
    total: docs.length,
    expired: docs.filter(d => docStatus(d).includes('expired')).length,
    expiringSoon: docs.filter(d => docStatus(d).includes('expiring') || docStatus(d).includes('soon')).length,
  }

  // ── PORT CALLS (this month) ──
  const pcThisMonth = portCalls.filter(p => inMonth(gv(p['ETA Date']) || gv(p['Arrival Date']), month))
  const portCallsSummary = {
    count: pcThisMonth.length,
    list: pcThisMonth.map(p => ({ port: gv(p['Port']) || gv(p['Title']), eta: gv(p['ETA Date']) || gv(p['Arrival Date']) })),
  }

  // ── CHARTERS (overlapping this month) + sea days ──
  const mStart = parseLocal(month + '-01')
  const mEnd = new Date(mStart.getFullYear(), mStart.getMonth() + 1, 0, 12)
  let charterDays = 0
  const charterList = []
  for (const c of charters) {
    const s = parseLocal(gv(c['Start Date'])), e = parseLocal(gv(c['End Date'])) || s
    if (!s) continue
    if (s <= mEnd && (e || s) >= mStart) {
      const from = s < mStart ? mStart : s
      const to = (e || s) > mEnd ? mEnd : (e || s)
      const days = Math.max(1, Math.round((to - from) / 86400000) + 1)
      charterDays += days
      charterList.push({ ref: gv(c['Charter Ref']) || gv(c['Lead Guest']) || gv(c['Title']) || 'Charter', days, revenue: moneyOf(c) })
    }
  }
  const charterRevenue = charterList.reduce((s, c) => s + (c.revenue || 0), 0)
  const chartersSummary = { count: charterList.length, days: charterDays, revenue: charterRevenue, list: charterList }

  // ── BUDGET (this month vs prorated annual) ── budget_actuals.month is the month NUMBER (1-12) of the line's year, as the
  // Budget page writes it; before fleet-2 this compared it with 'YYYY-MM' and the spend always came out 0 (budgetFigures)
  const linesForYear = budgetLinesRes.filter(l => l.year == null || String(l.year) === String(year))
  const lineIds = linesForYear.map(l => l.id)
  let actuals = []
  if (lineIds.length) {
    const { data } = await supabase.from('budget_actuals').select('*').in('line_id', lineIds)
    actuals = data || []
  }
  const budgetSummary = budgetFigures(linesForYear, actuals, month)

  // ── CREW ── on board = same rule as "who is on board?"; records = all crew rows not archived
  const cc = crewCounts(crewRes)
  const crewSummary = { headcount: cc.onBoard, onBoard: cc.onBoard, records: cc.records, byPosition: {} }

  // ── DRILLS (this month) ──
  const drillsThisMonth = drillsRes.filter(d => inMonth(d.drill_date, month))
  const drillsSummary = { thisMonth: drillsThisMonth.length, types: [...new Set(drillsThisMonth.map(d => d.drill_type).filter(Boolean))] }

  // ── RISK ASSESSMENTS ──
  const riskSummary = {
    total: riskRes.length,
    overdue: riskRes.filter(r => r.target_date && parseLocal(r.target_date) < new Date() && !['Complete', 'Approved'].includes(r.status)).length,
  }

  // ── LSA / FFE ──
  const lsaSummary = { total: lsaRes.length, defect: lsaRes.filter(i => (i.status || '').toLowerCase() === 'defect').length }

  // -- ENGINE HOURS & FUEL (counters are cumulative: month usage = max - min) --
  const HOURS_FIELDS = {
    'ME Port Hours': 'Main Engine (Port)',
    'ME Stbd Hours': 'Main Engine (Stbd)',
    'Gen Port Hours': 'Generator (Port)',
    'Gen Stbd Hours': 'Generator (Stbd)',
  }
  const numOf = (v) => {
    const n = Number(String(gv(v) ?? '').replace(/[^0-9.-]/g, ''))
    return isNaN(n) ? null : n
  }
  const logsThisMonth = (engineLog || [])
    .filter(r => inMonth(gv(r['Date']), month))
    .sort((a, b) => String(gv(a['Date'])).localeCompare(String(gv(b['Date']))))
  function spanOf(field) {
    const vals = logsThisMonth.map(r => numOf(r[field])).filter(v => v != null && v > 0)
    if (vals.length < 2) return 0
    const span = Math.max(...vals) - Math.min(...vals)
    return span > 0 ? Math.round(span * 10) / 10 : 0
  }
  const engineRun = Object.entries(HOURS_FIELDS)
    .map(([field, label]) => ({ label, hours: spanOf(field) }))
    .filter(e => e.hours > 0)
  const fuelVals = logsThisMonth.map(r => numOf(r['Fuel Total'])).filter(v => v != null && v > 0)
  let fuelUsed = 0
  if (fuelVals.length >= 2) {
    const nonDecreasing = fuelVals.every((v, i) => i === 0 || v >= fuelVals[i - 1])
    fuelUsed = nonDecreasing ? Math.max(...fuelVals) - Math.min(...fuelVals) : fuelVals.reduce((t, v) => t + v, 0)
  } else if (fuelVals.length === 1) {
    fuelUsed = fuelVals[0]
  }
  const engine = {
    entries: logsThisMonth.length,
    run: engineRun,
    totalHours: Math.round(engineRun.reduce((t, e) => t + e.hours, 0) * 10) / 10,
    fuel: Math.round(fuelUsed),
  }

  // -- CERTIFICATES & SURVEYS COMING DUE (next 12 months) --
  const horizon = new Date(mEnd.getFullYear(), mEnd.getMonth() + 12, mEnd.getDate(), 12)
  const soonCut = new Date(mEnd.getFullYear(), mEnd.getMonth() + 3, mEnd.getDate(), 12)
  const expiryOf = (d) => gv(d['Expiry Date']) || gv(d['Expires'])
  const dueDocs = docs
    .map(d => ({ title: gv(d['Title']), type: gv(d['Document Type']), when: parseLocal(expiryOf(d)), raw: expiryOf(d) }))
    .filter(x => x.when && x.when >= mStart && x.when <= horizon)
    .sort((a, b) => a.when - b.when)
  const surveysDue = {
    total: dueDocs.length,
    within90: dueDocs.filter(x => x.when <= soonCut).length,
    list: dueDocs.slice(0, 10).map(x => ({ title: x.title, type: x.type, date: String(x.raw).slice(0, 10) })),
  }

  // -- CREW BREAKDOWN --
  const posOf = (c) => c.position || c.rank || c.role || c['Position'] || c['Rank'] || ''
  const byPosition = {}
  for (const c of (crewRes || []).filter(isOnBoard)) {
    const p = String(posOf(c) || 'Crew').trim() || 'Crew'
    byPosition[p] = (byPosition[p] || 0) + 1
  }

  // -- PROJECTS (refit_items: yard period + in-service work, with quotes) --
  const num = (v) => (v == null || v === '' ? 0 : Number(v) || 0)
  const isDone = (st) => /complete|done|closed/i.test(String(st || ''))
  const projActive = (refitRes || []).filter(p => !isDone(p.status))
  const projDoneThisMonth = (refitRes || []).filter(p => isDone(p.status) && inMonth(p.updated_at, month))
  const projects = {
    active: projActive.length,
    completedThisMonth: projDoneThisMonth.length,
    quotedTotal: projActive.reduce((t, p) => t + num(p.quoted_amount), 0),
    approvedTotal: projActive.reduce((t, p) => t + num(p.approved_amount), 0),
    invoicedTotal: (refitRes || []).reduce((t, p) => t + num(p.invoiced_to_date), 0),
    list: projActive
      .slice()
      .sort((a, b) => num(b.quoted_amount) - num(a.quoted_amount))
      .slice(0, 12)
      .map(p => ({
        name: p.task_name || 'Project',
        contractor: p.contractor || '',
        location: p.location || '',
        status: p.status || '',
        target: p.target_date || '',
        quoted: num(p.quoted_amount),
        approved: num(p.approved_amount),
        invoiced: num(p.invoiced_to_date),
      })),
  }

  // -- QUOTES & INVOICES filed in Documents --
  const docType = (d) => gv(d['Document Type'])
  const quoteDocs = docs.filter(d => /quote/i.test(docType(d)))
  const invoiceDocs = docs.filter(d => /invoice/i.test(docType(d)))
  const docBrief = (d) => ({ title: gv(d['Title']), issued: gv(d['Issued Date']), url: gv(d['File URL']) })
  const quotes = {
    quoteCount: quoteDocs.length,
    invoiceCount: invoiceDocs.length,
    recentQuotes: quoteDocs.slice(-8).reverse().map(docBrief),
    recentInvoices: invoiceDocs.slice(-8).reverse().map(docBrief),
  }

  // -- LOOKING AHEAD (next month) --
  const nextMonthDate = new Date(mStart.getFullYear(), mStart.getMonth() + 1, 1, 12)
  const nextMonth = nextMonthDate.toISOString().slice(0, 7)
  const dueNext = tasks.filter(t => inMonth(gv(t['Next Service Date']), nextMonth))
  const chartersAhead = charters.filter(c => {
    const st = parseLocal(gv(c['Start Date']))
    return st && st > mEnd
  }).sort((a, b) => (parseLocal(gv(a['Start Date'])) - parseLocal(gv(b['Start Date'])))).slice(0, 6)
  const projectsAhead = projActive
    .filter(p => p.target_date && parseLocal(p.target_date) >= mStart)
    .sort((a, b) => parseLocal(a.target_date) - parseLocal(b.target_date))
    .slice(0, 8)
  const lookingAhead = {
    nextMonthLabel: nextMonthDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
    maintenanceDue: dueNext.length,
    maintenanceList: dueNext.map(t => gv(t['Task Name']) || gv(t['Name'])).filter(Boolean).slice(0, 10),
    charters: chartersAhead.map(c => ({
      ref: gv(c['Charter Ref']) || gv(c['Lead Guest']) || 'Charter',
      start: gv(c['Start Date']), end: gv(c['End Date']),
    })),
    projects: projectsAhead.map(p => ({ name: p.task_name || 'Project', target: p.target_date, quoted: num(p.quoted_amount), status: p.status || '' })),
  }

  crewSummary.byPosition = byPosition

  return {
    meta: {
      vessel: config?.vessel_name || 'Vessel',
      month,
      monthLabel: new Date(mStart).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
      generatedAt: new Date().toISOString(),
      flag: config?.flag || '', imo: config?.imo || '',
    },
    equipment: equipmentSummary,
    maintenance,
    inventory: inventorySummary,
    documents: documentsSummary,
    portCalls: portCallsSummary,
    charters: chartersSummary,
    budget: budgetSummary,
    crew: crewSummary,
    drills: drillsSummary,
    risk: riskSummary,
    lsa: lsaSummary,
    engine,
    surveysDue,
    projects,
    quotes,
    lookingAhead,
    compliance: complianceRes,
  }
}

// Build a compact, authoritative facts string for the AI intro (numbers only, no invention)
export function reportFactsForAI(d, kind) {
  const L = []
  L.push(`Vessel: ${d.meta.vessel}. Period: ${d.meta.monthLabel}. Report type: ${kind}.`)
  L.push(`Equipment: ${d.equipment.total} tracked, ${d.equipment.outOfService} out of service.`)
  L.push(`Maintenance: ${d.maintenance.overdue} overdue, ${d.maintenance.dueThisMonth} due this month.`)
  L.push(`Inventory: ${d.inventory.outOfStock} out of stock, ${d.inventory.lowStock} low.`)
  L.push(`Documents/certs: ${d.documents.expired} expired, ${d.documents.expiringSoon} expiring soon.`)
  L.push(`Port calls this month: ${d.portCalls.count}. Charters: ${d.charters.count} (${d.charters.days} days).`)
  if (kind === 'owner') L.push(`Budget: spent ${d.budget.spentThisMonth} vs monthly budget ${d.budget.monthlyBudget}; YTD ${d.budget.ytdSpent}.`)
  L.push(`Drills this month: ${d.drills.thisMonth}. Risk assessments overdue: ${d.risk.overdue}. LSA defects: ${d.lsa.defect}.`)
  L.push(crewFact({ onBoard: d.crew.onBoard ?? d.crew.headcount, records: d.crew.records ?? d.crew.headcount }))
  if (d.engine.totalHours > 0 || d.engine.fuel > 0) L.push(`Engine usage: ${d.engine.totalHours} hours run, ${d.engine.fuel} fuel units consumed.`)
  L.push(`Certificates/surveys due within 12 months: ${d.surveysDue.total} (${d.surveysDue.within90} within 90 days).`)
  L.push(`Projects in progress: ${d.projects.active} (quoted ${d.projects.quotedTotal}, approved ${d.projects.approvedTotal}, invoiced to date ${d.projects.invoicedTotal}). Completed this month: ${d.projects.completedThisMonth}.`)
  L.push(`Next month (${d.lookingAhead.nextMonthLabel}): ${d.lookingAhead.maintenanceDue} maintenance items due, ${d.lookingAhead.charters.length} charters booked, ${d.lookingAhead.projects.length} projects targeted.`)
  return L.join('\n')
}
